import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { toast } from "react-hot-toast";
import {
  StarIcon,
  SparklesIcon,
  HeartIcon,
  CheckBadgeIcon,
  BookOpenIcon,
  UserGroupIcon,
  TagIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import {
  StarIcon as StarIconSolid,
  HeartIcon as HeartIconSolid,
} from "@heroicons/react/24/solid";
import { Instructor } from "@/types";
import {
  useIsInstructorInWishlist,
  useToggleInstructorWishlist,
} from "@/hooks/useInstructorWishlist";

interface InstructorCardComponentProps {
  instructor: Instructor;
  simplified?: boolean; // New prop to hide detailed sections on home page
  mobileLayout?: "vertical" | "horizontal"; // New prop for mobile layout preference
}

const InstructorCard: React.FC<InstructorCardComponentProps> = ({
  instructor,
  simplified = false,
  mobileLayout = "vertical",
}) => {
  // Use React Query hooks
  const isWishlisted = useIsInstructorInWishlist(instructor._id);
  const toggleWishlist = useToggleInstructorWishlist();
  const [isLoading, setIsLoading] = useState(false);

  const handleWishlistToggle = async () => {
    if (isLoading || toggleWishlist.isPending) return;

    setIsLoading(true);
    const action = isWishlisted ? "remove" : "add";
    toggleWishlist.mutate(
      { instructorId: instructor._id, action },
      {
        onError: (error: Error) => {
          // Check if it's an authentication error
          if (
            error.message.includes("Authentication required") ||
            error.message.includes("Invalid token")
          ) {
            toast.error("Please log in to add instructors to your wishlist");
          } else {
            toast.error(error.message || "Failed to update wishlist");
          }
        },
        onSettled: () => setIsLoading(false),
      }
    );
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, index) => (
      <span key={index} className="inline-block">
        {index < Math.floor(rating) ? (
          <StarIconSolid className="w-3 h-3 sm:w-4 sm:h-4 text-[#ff7043]" />
        ) : (
          <StarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-[#ff7043]" />
        )}
      </span>
    ));
  };

  // Get instructor type labels for display
  const getInstructorTypeLabel = (type: string) => {
    const typeLabels: Record<string, string> = {
      yogaInstructor: "🧘‍♀️ Yoga",
      meditationCoach: "🧘‍♂️ Meditation",
      pranayamaCoach: "🌬️ Pranayama",
      zumbaInstructor: "💃 Zumba",
      nutritionist: "🥗 Nutrition",
      ayurvedaPractitioner: "🌿 Ayurveda",
      womenWellnessCoach: "🌸 Women's Wellness",
      selfDefenseInstructor: "🛡️ Self Defense",
    };
    return typeLabels[type] || type;
  };

  // Get class format labels for display - matching instructor profile options
  const getClassFormatLabel = (format: string) => {
    const formatLabels: Record<string, string> = {
      private: "1-to-1",
      group: "Group Class",
    };
    return formatLabels[format] || format;
  };

  // Check if instructor is verified by admin (Blue Tick)
  const isFullyVerified = () => {
    return instructor.isVerified === true;
  };

  // Get active class count
  const getActiveClassCount = () => {
    // Use activeClassCount from API if available
    const count = instructor.activeClassCount || 0;
    return count;
  };

  // Get total students count
  const getTotalStudents = () => {
    return (
      instructor.stats?.totalStudentsTaught ||
      instructor.stats?.totalReviews ||
      0
    );
  };

  // Check if instructor has an active discount
  const getActiveDiscount = () => {
    // This would check for active promotions/discounts
    // For now, return null or implement based on your discount system
    return null; // Replace with actual discount logic if available
  };

  const activeClassCount = getActiveClassCount();

  // Determine layout classes based on mobileLayout prop
  const isHorizontal = mobileLayout === "horizontal";

  return (
    <div
      className={`group relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden transform hover:-translate-y-1 h-full flex ${isHorizontal ? "flex-row sm:flex-col" : "flex-col"}`}
    >
      {/* Favorite Button - Top Right */}
      <button
        onClick={handleWishlistToggle}
        disabled={isLoading}
        className={`absolute ${isHorizontal ? "top-2 right-2 sm:top-3 sm:right-3" : "top-3 right-3"} z-20 p-1.5 sm:p-2 rounded-full bg-white/95 hover:bg-white shadow-md hover:shadow-lg transition-all duration-300 group/heart`}
        title={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
      >
        {isLoading ? (
          <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-[#ff7043] border-t-transparent rounded-full animate-spin" />
        ) : isWishlisted ? (
          <HeartIconSolid className="w-4 h-4 sm:w-5 sm:h-5 text-[#ff7043] group-hover/heart:scale-110 transition-transform" />
        ) : (
          <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 group-hover/heart:text-[#ff7043] group-hover/heart:scale-110 transition-all" />
        )}
      </button>

      {/* Discount Badge - Top Left (if applicable) */}
      {getActiveDiscount() && (
        <div
          className={`absolute ${isHorizontal ? "top-2 left-2 sm:top-3 sm:left-3" : "top-3 left-3"} z-10 bg-gradient-to-r from-red-500 to-red-600 text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-lg`}
        >
          <TagIcon className="w-3 h-3 inline mr-1" />
          {getActiveDiscount()}% OFF
        </div>
      )}

      {/* Profile Image Section with Verification Badge */}
      <div
        className={`relative bg-gradient-to-br from-orange-50 to-orange-100 ${isHorizontal ? "w-1/3 sm:w-full h-auto sm:h-52 min-h-[140px]" : "h-52 w-full"}`}
      >
        {/* Active Classes Badge - Over Image */}
        {activeClassCount > 0 && (
          <div
            className={`absolute ${isHorizontal ? "bottom-2 left-1/2 -translate-x-1/2 sm:bottom-auto sm:top-3 sm:left-3 sm:translate-x-0" : "top-3 left-3"} z-10 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-bold shadow-md bg-gradient-to-r from-green-500 to-green-600 text-white flex items-center gap-1 whitespace-nowrap`}
          >
            <BookOpenIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className={isHorizontal ? "hidden sm:inline" : ""}>
              {activeClassCount} {activeClassCount === 1 ? "Class" : "Classes"}
            </span>
            <span className={isHorizontal ? "sm:hidden" : "hidden"}>
              {activeClassCount}
            </span>
          </div>
        )}

        {/* Profile Image - Centered */}
        <div
          className={`absolute inset-0 flex items-center justify-center ${isHorizontal ? "p-2 sm:pt-2 sm:pb-8" : "pt-2 pb-8"}`}
        >
          <div className="relative">
            {instructor.profileImage ? (
              <div
                className={`${isHorizontal ? "w-20 h-20 sm:w-32 sm:h-32" : "w-32 h-32"} relative rounded-full ring-2 sm:ring-4 ring-white shadow-xl overflow-hidden`}
              >
                <Image
                  src={instructor.profileImage}
                  alt={instructor.name}
                  fill
                  className="object-cover"
                />
              </div>
            ) : (
              <div
                className={`${isHorizontal ? "w-20 h-20 sm:w-32 sm:h-32" : "w-32 h-32"} rounded-full bg-gradient-to-br from-[#ff7043] to-[#ff8a65] flex items-center justify-center ring-2 sm:ring-4 ring-white shadow-xl`}
              >
                <span
                  className={`${isHorizontal ? "text-3xl sm:text-5xl" : "text-5xl"} font-bold text-white`}
                >
                  {instructor.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}

            {/* Experience Badge - Always show */}
            <div className="absolute -bottom-1 -left-1 bg-gradient-to-r from-[#ff7043] to-[#ff8a65] text-white rounded-full px-2 sm:px-2.5 py-0.5 sm:py-1 shadow-lg ring-1 sm:ring-2 ring-white">
              <span className="text-[10px] sm:text-xs font-bold">
                {instructor.experience}Y
              </span>
            </div>

            {/* Verification Badge on Image - Only show if email AND phone verified */}
            {isFullyVerified() && (
              <div
                className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full p-1 sm:p-1.5 shadow-lg ring-1 sm:ring-2 ring-white"
                title="Verified Instructor"
              >
                <CheckBadgeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            )}
          </div>
        </div>

        {/* Specialties - Bottom of image section, scrollable - HIDDEN on mobile horizontal view to save space */}
        {!simplified &&
          instructor.instructorType &&
          instructor.instructorType.length > 0 && (
            <div
              className={`absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200/50 py-2 px-3 ${isHorizontal ? "hidden sm:block" : "block"}`}
            >
              <div
                className="flex gap-2 overflow-x-auto scroll-smooth"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {instructor.instructorType.map((type: string) => (
                  <span
                    key={type}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-[#ff7043] border border-orange-200 whitespace-nowrap flex-shrink-0"
                  >
                    <SparklesIcon className="w-3 h-3 mr-1" />
                    {getInstructorTypeLabel(type)}
                  </span>
                ))}
              </div>
              <style jsx>{`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
            </div>
          )}
      </div>

      {/* Card Content */}
      <div
        className={`p-3 sm:p-4 ${isHorizontal ? "w-2/3 sm:w-full pl-2" : "w-full pt-2"} flex-1 flex flex-col justify-center sm:justify-start`}
      >
        {/* Name and Rating */}
        <div
          className={`${isHorizontal ? "text-left sm:text-center" : "text-center"} mb-1 sm:mb-2`}
        >
          <div
            className={`flex items-center ${isHorizontal ? "justify-start sm:justify-center" : "justify-center"} gap-2 mb-1`}
          >
            <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-[#ff7043] transition-colors line-clamp-1">
              {instructor.name}
            </h3>
            {getTotalStudents() > 0 && (
              <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold bg-green-50 text-green-700 border border-green-200 whitespace-nowrap">
                <UserGroupIcon className="w-3 h-3 mr-1" />
                {getTotalStudents()}
              </span>
            )}
          </div>
          <div
            className={`flex items-center ${isHorizontal ? "justify-start sm:justify-center" : "justify-center"} gap-2`}
          >
            {(instructor.stats?.averageRating || 0) > 0 && (
              <>
                <div className="flex items-center">
                  {renderStars(instructor.stats?.averageRating || 0)}
                </div>
                <span className="text-xs sm:text-sm font-semibold text-gray-700">
                  {(instructor.stats?.averageRating || 0).toFixed(1)}
                </span>
                <span className="text-[10px] sm:text-xs text-gray-500">
                  ({instructor.stats?.totalReviews || 0})
                </span>
              </>
            )}
          </div>
        </div>

        {/* Class Formats - Hidden on mobile horizontal to save space */}
        {!simplified &&
          instructor.classFormats &&
          instructor.classFormats.length > 0 && (
            <div
              className={`mb-2 sm:mb-3 ${isHorizontal ? "hidden sm:block" : "block"}`}
            >
              <div className="flex flex-wrap justify-center gap-2">
                {instructor.classFormats.map((format: string) => (
                  <span
                    key={format}
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200"
                  >
                    {getClassFormatLabel(format)}
                  </span>
                ))}
              </div>
            </div>
          )}

        {/* Bio - 3 Lines Max - Visible on mobile horizontal now */}
        <p
          className={`text-xs sm:text-sm text-gray-600 text-center mb-3 line-clamp-2 sm:line-clamp-3 leading-relaxed`}
        >
          {instructor.bio ||
            "Experienced instructor dedicated to helping students achieve their wellness goals through personalized guidance."}
        </p>

        {/* Mobile Horizontal Specific: Specialties (Simplified) */}
        {isHorizontal &&
          !simplified &&
          instructor.instructorType &&
          instructor.instructorType.length > 0 && (
            <div className="sm:hidden mb-2 flex flex-wrap gap-1">
              {instructor.instructorType.slice(0, 2).map((type: string) => (
                <span
                  key={type}
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-orange-50 text-[#ff7043] border border-orange-200"
                >
                  {getInstructorTypeLabel(type).split(" ")[1] ||
                    getInstructorTypeLabel(type)}
                </span>
              ))}
              {instructor.instructorType.length > 2 && (
                <span className="text-[10px] text-gray-500 self-center">
                  +{instructor.instructorType.length - 2}
                </span>
              )}
            </div>
          )}

        {/* Dual Action Buttons */}
        <div className="flex gap-2 mt-auto">
          <Link
            href={`/classes?instructor=${instructor._id}`}
            className="flex-1 flex items-center justify-center gap-2 bg-[#ff7043] text-white text-xs py-2.5 px-4 rounded-lg hover:bg-[#ff7043]/90 transition-all duration-200 transform hover:scale-[1.02]"
          >
            <BookOpenIcon className="w-4 h-4" />
            Classes
          </Link>
          <Link
            href={`/instructors/${instructor._id}`}
            className="flex-1 flex items-center justify-center gap-2 border border-[#463a33] text-[#463a33] text-xs py-2.5 px-4 rounded-lg hover:bg-[#463a33]/5 transition-all duration-200"
          >
            <UserIcon className="w-4 h-4" />
            Profile
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InstructorCard;
